import {
    Box,
    List,
    ListItemButton,
    Tooltip,
    Typography,
    type Theme,
} from "@mui/material";
import type { ReactElement } from "react";
import { KeyRound, LogOut } from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import { routePaths } from "../../routes/navigation";
import AppShellNavTree from "./AppShellNavTree";
import SidebarMessagesIndicator from "./SidebarMessagesIndicator";
import { getSidebarGreetingName } from "./appShell.utils";

function collapsedNavTooltip(label: string, show: boolean, node: ReactElement) {
    if (!show) {
        return node;
    }

    return (
        <Tooltip title={label} placement="right" arrow enterDelay={200}>
            {node}
        </Tooltip>
    );
}

interface AppShellSidebarProps {
    desktopCollapsed: boolean;
    isMobile: boolean;
    pathname: string;
    search: string;
    onBrandClick: () => void;
    onNavigate: (path: string) => void;
    onLogout: () => void;
    theme: Theme;
}

export default function AppShellSidebar({
    desktopCollapsed,
    isMobile,
    pathname,
    search,
    onBrandClick,
    onNavigate,
    onLogout,
    theme,
}: AppShellSidebarProps) {
    const showCollapsedTooltips = desktopCollapsed && !isMobile;
    const userName = useAppSelector((state) => state.auth.user?.name);
    const greetingName = getSidebarGreetingName(userName);

    return (
        <Box
            className={showCollapsedTooltips ? "app-shell__sidebar app-shell__sidebar--collapsed" : "app-shell__sidebar"}
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                gap: showCollapsedTooltips ? 1.5 : 2,
            }}
        >
            <Box
                className="app-shell__brand"
                onClick={onBrandClick}
                sx={{
                    display: "flex",
                    alignItems: desktopCollapsed && !isMobile ? "center" : "flex-start",
                    justifyContent: desktopCollapsed && !isMobile ? "center" : "flex-start",
                    gap: 2,
                    cursor: !isMobile ? "pointer" : "default",
                }}
            >
                <Box
                    className="app-shell__brandMark"
                    sx={{
                        minWidth: showCollapsedTooltips ? 40 : 48,
                        minHeight: showCollapsedTooltips ? 40 : 48,
                        width: showCollapsedTooltips ? 40 : 48,
                        height: showCollapsedTooltips ? 40 : 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        fontWeight: 800,
                        userSelect: "none",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                    }}
                >
                    IH
                </Box>

                {(!desktopCollapsed || isMobile) && (
                    <Box sx={{ display: "grid", gap: 0.25, minWidth: 0 }}>
                        <Typography variant="overline" color="primary.main" sx={{ display: "block", lineHeight: 1.2 }}>
                            InHouse CRM + BI
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                            Панел за управление
                        </Typography>
                        {greetingName ? (
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                    mt: 0.25,
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    textDecoration: "underline",
                                    textDecorationThickness: "2px",
                                    textUnderlineOffset: "3px",
                                }}
                            >
                                Здравей, {greetingName}
                            </Typography>
                        ) : null}
                        <SidebarMessagesIndicator
                            collapsed={false}
                            onNavigate={onNavigate}
                        />
                    </Box>
                )}
            </Box>

            {showCollapsedTooltips ? (
                <SidebarMessagesIndicator collapsed onNavigate={onNavigate} />
            ) : null}

            <List
                disablePadding
                className="app-shell__navList"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pr: showCollapsedTooltips ? 0 : 0.5,
                    width: "100%",
                }}
            >
                <AppShellNavTree
                    desktopCollapsed={desktopCollapsed}
                    isMobile={isMobile}
                    pathname={pathname}
                    search={search}
                    onNavigate={onNavigate}
                    theme={theme}
                />
            </List>

            <Box sx={{ pt: showCollapsedTooltips ? 1 : 2, borderTop: 1, borderColor: "divider" }}>
                {collapsedNavTooltip(
                    "Смени парола",
                    showCollapsedTooltips,
                    <ListItemButton
                        onClick={() => onNavigate(routePaths.changePassword)}
                        sx={{
                            borderRadius: 2,
                            justifyContent: showCollapsedTooltips ? "center" : "flex-start",
                            px: showCollapsedTooltips ? 0.75 : 2,
                        }}
                    >
                        <KeyRound size={18} style={{ marginRight: showCollapsedTooltips ? 0 : 12 }} />
                        {(!desktopCollapsed || isMobile) && <Typography>Смени парола</Typography>}
                    </ListItemButton>,
                )}
                {collapsedNavTooltip(
                    "Изход",
                    showCollapsedTooltips,
                    <ListItemButton
                        onClick={onLogout}
                        sx={{
                            borderRadius: 2,
                            color: "error.main",
                            justifyContent: showCollapsedTooltips ? "center" : "flex-start",
                            px: showCollapsedTooltips ? 0.75 : 2,
                        }}
                    >
                        <LogOut size={18} style={{ marginRight: showCollapsedTooltips ? 0 : 12 }} />
                        {(!desktopCollapsed || isMobile) && <Typography>Изход</Typography>}
                    </ListItemButton>,
                )}
            </Box>
        </Box>
    );
}
